// @flow
import React from 'react'
import ReactDOM from 'react-dom'
import Dropzone from 'react-dropzone'
import Chip from '@material-ui/core/Chip'
import Divider from '@material-ui/core/Divider'
import ExpansionPanel from '@material-ui/core/ExpansionPanel'
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails'
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary'
import IconButton from '@material-ui/core/IconButton'
import MenuItem from '@material-ui/core/MenuItem'
import Select from '@material-ui/core/Select'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import { withStyles } from '@material-ui/core/styles'
import type { SitesType } from '../reducers/public_sites'
import GroupPanel from './group_panel'
import * as grouping from './mapsources_grouping'
import PublicSiteChooser from './public_site_chooser'
import TimeseriesChooser from './timeseries_source_chooser'
import type { ActionsType, MapType } from '../reducers/map'
import type { MapsourcesType, UserMapsourceType } from '../reducers/mapsources'
import { getMapserver } from '../utils'

export type PropsType = {
    baseImageURL: string,
    er2Map: any,
    loadingTileCounts: {},
    map: MapType,
    mapsourceIsLoading: Function,
    mapsources: MapsourcesType,
    mask: 'layers' | 'tables',
    onAddPublicServices: Function,
    onAddTimeseriesSource: Function,
    onUpdateGroupLayerOrdering: Function,
    publicSites: SitesType,
    theme: {},
    updateMapsourceOrder: Function,
    ...ActionsType,
}

// A group represents a mixed collection of userlayers
export type GroupType = {
    ...UserMapsourceType,
}

export type GroupingType = 'none' | 'layerType'

export type OrderingType = 'none' | 'asc' | 'desc'

type StateType = {
    filteredLayerCount: number,
    filters: string[],
    groups: Array<GroupType>,
    groupingType: GroupingType,
    newFilter: string,
    ordering: OrderingType,
    showPublicSiteChooser: boolean,
    showTimeseriesChooser: boolean,
    totalLayerCount: number,
}

const styles = {
    bigger: {
        fontSize: 'larger',
    },
    filterField: {
        marginLeft: '6px',
        marginTop: '6px',
        width: '50%',
    },
    filterResult: {
        float: 'right',
        marginRight: '6px',
        marginTop: '6px',
    },
}

// eslint-disable-next-line
class MapsourcesPanel extends React.Component<PropsType, StateType> {
    state = {
        filters: [],
        filteredLayerCount: -1,
        groupingType: 'none',
        loadingTileCounts: {},
        newFilter: '',
        ordering: 'none',
        showPublicDataChooser: false,
    }

    static getDerivedStateFromProps(props: PropsType, state: StateType) {
        const groups = grouping.getGrouping(props.mask, state.groupingType, state.ordering, props)
        let totalLayerCount = 0
        groups.forEach((g) => {
            totalLayerCount += g.layers.length
        })
        const filteredGroups = grouping.filterGroups(groups, state.filters)
        console.log('updating grouping')
        groups.forEach((g) => {
            console.log(g.layers.map(l => l.name))
        })
        console.log('done updating grouping')
        return { groups: filteredGroups, totalLayerCount }
    }

    onAddTimeseriesSource = ({ mongoHost, mongoDatabase, collection }) => {
        this.setState({ showTimeseriesChooser: false })
        this.props.onAddTimeseriesSource(mongoHost, mongoDatabase, collection)
    }

    render() {
        /**
         * The ordering within a group has changed. Update mapfile.
         * @param groupName
         * @param layers
         */
        const onChangeSortOrderForGroup = (groupName: string, layers: []) => {
            let layersOrdered = []
            const groups = []
            this.state.groups.forEach((group) => {
                let groupCopy = group
                if (groupName === group.name) {
                    // The input layers list is a subset of the layer object, so replace each one with their actual layer object
                    const layersComplete = layers.map(sortedLayer => group.layers.find(groupLayer => groupLayer.name === sortedLayer.name))
                    layersOrdered = [...layersOrdered, ...layersComplete.map(l => ({ name: l.name, mapsource: l.mapsource }))]
                    groupCopy = { ...group, layers: layersComplete }
                } else layersOrdered = [...layersOrdered, ...group.layers]
                groups.push(groupCopy)
            })
            this.setState({ groups })

            // Second ordering is public layers and the userlayers. Userlayers are one entity for our purposes because
            //   they are all represented by a single mapfile.
            const mapsourcesOrdered = []
            layers.forEach((l) => {
                const mapsource = this.props.mapsources.mapsources.find(m => m.name === l.mapsource)
                if (mapsourcesOrdered.indexOf(mapsource.name) < 0) mapsourcesOrdered.push(mapsource.name)
            })
            // Append missing mapsources
            this.props.mapsources.mapsources.forEach((m) => {
                if (mapsourcesOrdered.indexOf(m.name) < 0) mapsourcesOrdered.push(m.name)
            })

            this.props.onUpdateGroupLayerOrdering(layersOrdered, mapsourcesOrdered)
        }

        /**
         * Recalculate grouping
         * @param e New group type
         */
        const updateGrouping = (e) => {
            let groupingType = e.target.value
            let ordering = this.state.ordering
            if (groupingType === 'asc' || groupingType === 'desc') {
                ordering = groupingType
                groupingType = this.state.groupingType
            }
            const groups = grouping.getGrouping(this.props.mask, groupingType, ordering, this.props)
            const filteredGroups = grouping.filterGroups(groups, this.state.filters)
            let filteredLayerCount = 0
            filteredGroups.forEach((g) => {
                filteredLayerCount += g.layers.length
            })

            this.setState({ groupingType, ordering, groups: filteredGroups, filteredLayerCount })

            // Update mapfile ordering
            let layersOrdered = []
            groups.forEach((group) => {
                layersOrdered = [...layersOrdered, ...group.layers]
            })
            this.props.onUpdateGroupLayerOrdering(layersOrdered)
        }

        const getOrderingText = (type) => {
            const txt = {
                asc: 'Ascending',
                desc: 'Descending',
            }
            if (this.state.ordering === type) return (<em style={{ fontWeight: 'bold' }}>{txt[type]}</em>)
            return (<em>{txt[type]}</em>)
        }

        const littleButtonStyle = {
            fontSize: '15px',
            padding: '0px',
            minHeight: '0px',
            minWidth: '0px',
            height: '30px',
            width: '30px',
        }
        const onDrop = (acceptedFiles, rejectedFiles) => {
            // Upload to first mapserver mapsource
            const mapserver = getMapserver(this.props.mapsources)
            this.props.onUserLayersAdd(mapserver.name, acceptedFiles, rejectedFiles)
        }

        const onClickFilter = () => {
            this.setState({ showFilter: !this.state.showFilter })
        }

        const onClickPublicData = () => {
            this.setState({ showPublicSiteChooser: !this.state.showPublicSiteChooser, showTimeseriesChooser: false })
        }

        const onClickTimeseries = () => {
            this.setState({ showTimeseriesChooser: !this.state.showTimeseriesChooser, showPublicSiteChooser: false })
        }

        let filterHTML = null
        if (this.state.showFilter) {
            const hasFilter = filter => this.state.filters.indexOf(filter) >= 0
            const addFilter = (filter) => {
                if (filter && !hasFilter(filter)) {
                    const filters = [...this.state.filters]
                    filters.push(filter)
                    filters.sort()

                    // TODO: Make it so that the groups don't have to get regenerated.
                    const groups = grouping.getGrouping(this.props.mask, this.state.groupingType, this.state.ordering, this.props)
                    const filteredGroups = grouping.filterGroups(groups, filters)
                    let filteredLayerCount = 0
                    filteredGroups.forEach((g) => {
                        filteredLayerCount += g.layers.length
                    })

                    this.setState({ filters, groups: filteredGroups, filteredLayerCount })
                }
            }
            const handleFilterInput = (input) => {
                if (input.split(' ').length > 1) {
                    let filterWord = input.split(' ')[0]
                    filterWord = filterWord.replace(/\W/g, '')
                    addFilter(filterWord)
                    this.setState({ newFilter: '' })
                } else {
                    input = input.replace(/\W/g, '')
                    this.setState({ newFilter: input })
                }
            }
            const deleteFilter = (filter) => {
                const i = this.state.filters.indexOf(filter)
                const filters = [...this.state.filters.slice(0, i), ...this.state.filters.slice(i + 1)]
                // TODO: Make it so that the groups don't have to get regenerated.
                const groups = grouping.getGrouping(this.props.mask, this.state.groupingType, this.state.ordering, this.props)
                const filteredGroups = grouping.filterGroups(groups, filters)
                let filteredLayerCount = 0
                filteredGroups.forEach((g) => {
                    filteredLayerCount += g.layers.length
                })
                this.setState({ filters, groups: filteredGroups, filteredLayerCount })
            }

            // Use same setup as filters in layer properties
            const filters = this.state.filters.map(filter => (
                <Chip
                    key={filter}
                    label={filter}
                    onDelete={() => deleteFilter(filter)}
                    className={this.props.theme.tag}
                />
            ))

            filterHTML = (
                <div>
                    <TextField
                        className={this.props.classes.filterField}
                        label={'Add layer filter'}
                        type="text"
                        value={this.state.newFilter}
                        onChange={e => handleFilterInput(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.keyCode === 13 && e.shiftKey === false) {
                                e.preventDefault()
                                handleFilterInput(`${this.state.newFilter} `)
                            }
                        }}
                    />
                    <div>
                        {filters}
                        {filters.length > 0 && (
                            <span
                                className={this.props.classes.filterResult}
                            >
                                {this.state.filteredLayerCount} / {this.state.totalLayerCount}
                            </span>)
                        }
                    </div>
                </div>
            )
        }

        let publicSiteChooser = null
        if (this.state.showPublicSiteChooser) {
            const { top, left } = this.ref.getBoundingClientRect()
            const root = document.getElementById('root')
            publicSiteChooser = ReactDOM.createPortal((
                <PublicSiteChooser
                    baseImageURL={this.props.baseImageURL}
                    getPublicSites={this.props.getPublicSites}
                    mapsources={this.props.mapsources}
                    onClose={() => this.setState({ showPublicSiteChooser: false })}
                    onSelect={this.props.onAddPublicServices}
                    pos={{ top, right: root.getBoundingClientRect().right - left }}
                    publicSites={this.props.publicSites}
                />
            ), root)
        }

        let timeseriesChooser = null
        if (this.state.showTimeseriesChooser) {
            const { top, left } = this.ref.getBoundingClientRect()
            const root = document.getElementById('root')
            timeseriesChooser = ReactDOM.createPortal((
                <TimeseriesChooser
                    baseImageURL={this.props.baseImageURL}
                    mapsources={this.props.mapsources}
                    onClose={() => this.setState({ showTimeseriesChooser: false })}
                    onSelect={this.onAddTimeseriesSource}
                    pos={{ top, right: root.getBoundingClientRect().right - left }}
                />
            ), root)
        }

        return (
            <div
                ref={(el) => {
                    this.ref = el
                }}
            >
                <div>
                    <Dropzone
                        onDrop={onDrop}
                        style={{ border: '1px dashed gray', display: 'inline-block', margin: '6px' }}
                    >
                        <IconButton
                            style={littleButtonStyle}
                        >
                            <i className={'ms ms-layer-add'} role="button" />
                        </IconButton>
                    </Dropzone>
                    <IconButton
                        color={this.state.showPublicSiteChooser ? 'secondary' : undefined}
                        onClick={onClickPublicData}
                        style={littleButtonStyle}
                    >
                        <img
                            alt={'Add public'}
                            title="Add from public data source"
                            src={'/static/er2_map/img/add-database-24.png'}
                        />
                    </IconButton>
                    <IconButton
                        color={this.state.showTimeseriesChooser ? 'secondary' : undefined}
                        onClick={onClickTimeseries}
                        style={littleButtonStyle}
                    >
                        <img
                            alt={'Add timeseries'}
                            title="Add timeseries data from CSIP mongodb service"
                            src={'/static/er2_map/img/if_stopwatch_83011.png'}
                        />
                    </IconButton>
                    <span
                        style={{
                            display: 'inline-block',
                            margin: '6px 10px 0 0',
                            fontSize: 'smaller',
                            float: 'right',
                        }}
                    >
                        <span>Grouping: </span>
                        <Select
                            onChange={updateGrouping}
                            value={this.state.groupingType}
                            inputProps={{
                                name: 'Type',
                                id: 'type-simple',
                            }}
                        >
                            <MenuItem value={'none'}><em>None</em></MenuItem>
                            <MenuItem value={'category'}>Category</MenuItem>
                            <MenuItem value={'date_modified'}>Date Modified</MenuItem>
                            {this.props.mask === 'layers' && (<MenuItem value={'layerType'}>Layer Type</MenuItem>)}
                            <MenuItem value={'tag'}>Tag</MenuItem>
                            <Divider style={{ height: '8px' }} />
                            <MenuItem value={'asc'}>{getOrderingText('asc')}</MenuItem>
                            <MenuItem value={'desc'}>{getOrderingText('desc')}</MenuItem>
                        </Select>
                        <IconButton
                            color={this.state.showFilter ? 'secondary' : undefined}
                            onClick={onClickFilter}
                            style={{ ...littleButtonStyle }}
                        >
                            <i className={'fa fa-filter'} role="button" />
                        </IconButton>
                    </span>
                </div>

                {filterHTML}

                <div className={this.props.theme.layerPropsDiv}>
                    {publicSiteChooser}
                </div>

                <div className={this.props.theme.layerPropsDiv}>
                    {timeseriesChooser}
                </div>

                <div
                    style={{
                        backgroundColor: '#eeeeee',
                        padding: '2px',
                        marginTop: '5px',
                        height: 'calc(100vh - 160px)',
                        overflowY: 'auto',
                    }}
                >
                    {this.state.groups.map((group, index) => (
                        <ExpansionPanel
                            defaultExpanded
                            key={group.name}
                        >
                            <ExpansionPanelSummary>
                                <Typography
                                    classes={{ root: this.props.classes.bigger }}
                                    color="secondary"
                                >{group.name}
                                </Typography>
                            </ExpansionPanelSummary>
                            <ExpansionPanelDetails style={{ backgroundColor: '#eeeeee' }}>
                                <GroupPanel
                                    baseImageURL={this.props.baseImageURL}
                                    er2Map={this.props.er2Map}
                                    group={group}
                                    loadingTileCounts={this.props.loadingTileCounts}
                                    map={this.props.map}
                                    mapsources={this.props.mapsources}
                                    onChangeSortOrderForGroup={onChangeSortOrderForGroup}
                                    theme={this.props.theme}
                                    {...this.props}
                                />
                            </ExpansionPanelDetails>
                        </ExpansionPanel>
                    ))}
                </div>
            </div>
        )
    }
}

export default withStyles(styles)(MapsourcesPanel)
